/**
 * Business constants for bike leasing calculator
 *
 * This file contains all business-critical constants that:
 * - Represent business rules and validation limits
 * - Require periodic updates (e.g., annual tax year changes)
 * - Define default values for calculator inputs
 */

/**
 * Health insurance additional contribution rate (in percent)
 *
 * IMPORTANT: Must be updated annually for new tax year
 *
 * Historical values:
 * - 2024: 1.7%
 * - 2025: 2.5%
 * - 2026: 2.9%
 *
 * Source: GKV-Spitzenverband (Association of Statutory Health Insurance Funds)
 */
export const HEALTH_INSURANCE_ADDITIONAL_RATE = 2.9;

/**
 * Default bike prices by type (in EUR)
 *
 * These values are used as initial defaults when the calculator loads
 */
export const DEFAULT_BIKE_PRICES = {
  DEFAULT_BIKE: 2500,
  E_BIKE: 4000,
  CARGO_BIKE: 5500,
} as const;

/**
 * Default gross salary for initial calculator state (in EUR)
 *
 * Used as the starting value when no user input is provided
 */
export const DEFAULT_GROSS_SALARY = 3800;

/**
 * Salary validation limits (in EUR)
 *
 * MIN: Minimum monthly gross salary accepted by the calculator
 * MAX: Maximum monthly gross salary accepted by the calculator
 */
export const SALARY_LIMITS = {
  MIN: 197,
  MAX: 100000,
} as const;

/**
 * Maximum monthly amount (in EUR) accepted by the budget calculator input fields.
 *
 * Applied to both:
 * - the gross salary conversion amount (Umwandlungsbetrag, `monthlyBudgetBrutto`)
 * - the employer grant (Arbeitgeberzuschuss, `arbeitgeberZuschuss`)
 *
 * Inclusive upper bound: a value of 500 is valid; 501 shows a validation error.
 * Applied as client-side input validation only — the calculation engine does not
 * enforce this limit.
 */
export const BUDGET_MONTHLY_MAX = 500;

/**
 * UVP (recommended retail price) deviation threshold factor
 *
 * Triggers a warning if UVP exceeds purchase price by more than this factor
 * Current value: 1.3 means 30% deviation is allowed before warning
 *
 * Example: If purchase price is €1000, warning shows if UVP > €1300
 */
export const UVP_DEVIATION_THRESHOLD = 1.3;

/**
 * Standard leasing contract duration in years
 *
 * Used for calculating annual costs and depreciation
 */
export const LEASING_DURATION_YEARS = 3;
